import { useEffect, useState } from "react";
import {beginSession} from "../../config/token-refresh";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input, message, Space, Typography } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { login } from "./api-login";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {useQueryClient} from "@tanstack/react-query";
import {useAppTheme} from "../../config/theme-context";
import {PASSWORD_SCREEN, setMustChangePassword} from "../../config/must-change-password";
import {hasSession, homeForRole} from "../../config/protected-route";
import {clearAuthSession} from "../../config/auth-session";

const { Text } = Typography;

const Login = () => {
    const navigate = useNavigate();

    // Already signed in (another tab, the installed app, a notification opening "/login"): do not ask
    // again — that is what operators experienced as being thrown out of the console.
    useEffect(() => {
        // Sent here because the session is over: whatever tokens are still lying around are the
        // dead ones, and forwarding on them is the loop this guard exists to break. Clear, show
        // the form, and drop the reason from the address so a later reload starts clean.
        let expired = false;
        try {
            expired = new URLSearchParams(window.location.search).get("reason") === "session-expired";
        } catch (e) { /* ignore */ }
        if (expired) {
            clearAuthSession();
            try { window.history.replaceState(null, "", "/login"); } catch (e) { /* ignore */ }
            return;
        }
        if (hasSession()) {
            let role = "OPERATOR";
            try { role = localStorage.getItem("role") || "OPERATOR"; } catch (e) { /* ignore */ }
            navigate(homeForRole(role), {replace: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { t } = useTranslation();
    const {branding} = useAppTheme();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient();

    const handleLoginPassword = async () => {
        if (!password) {
            message.error(t("login.not_fill_fields") || "Please enter password");
            return;
        }
        try {
            setLoading(true);
            const response = await login(username, password);
            if (response?.status !== 200) {
                message.error(t("login.invalid_credential") || "Invalid credentials");
            } else {
                await queryClient.invalidateQueries();
                localStorage.setItem("access-token", response?.data?.accessToken);
                localStorage.setItem("refresher-token", response?.data?.refreshToken);
                // Re-arm token renewal. Ending a session clears the renewal timer, and when that
                // happened while this tab was already on /login there was no page load to reset it —
                // so without this the operator would sign in successfully and then never have their
                // token renewed again.
                beginSession();

                // The backend answers this once, here: is this account still using a password
                // somebody else chose for it? Everyone is, on their first sign-in after the fix.
                const mustChange = !!response?.data?.mustChangePassword;
                setMustChangePassword(mustChange);

                try {
                    const decoded = jwtDecode(response?.data?.accessToken);
                    const role = decoded.role || 'OPERATOR';
                    localStorage.setItem("role", role);
                    if (mustChange) {
                        navigate(PASSWORD_SCREEN);
                    } else if (role === 'OPERATOR') {
                        navigate("/dashboard/operator-home");
                    } else if (role === 'SUPERVISOR') {
                        navigate("/dashboard/chats");
                    } else {
                        navigate("/dashboard/admin-home");
                    }
                } catch {
                    navigate(mustChange ? PASSWORD_SCREEN : "/dashboard/operator-home");
                }
            }
        } catch (e) {
            // "Wrong username or password" is a claim about the credentials, and this used to be
            // said for every possible failure — including the ones where the request never left the
            // browser. An operator reported being unable to get back in and being told their
            // password was wrong; the server's log for that day shows seven sign-ins and seven
            // successes, and not one rejected attempt. Their attempts were never arriving, and the
            // console was blaming the password for it, which sends somebody into retyping a
            // credential that was correct all along.
            //
            // Only the server gets to say the credentials are wrong.
            const serverAnswered = !!e?.response;
            const rejected = serverAnswered && [400, 401, 403].includes(e.response.status);

            if (rejected) {
                message.error(t("login.invalid_credential") || "Invalid credentials");
            } else if (serverAnswered) {
                message.error(
                    e?.response?.data?.message ||
                    "ورود ممکن نشد. لطفاً چند لحظه بعد دوباره تلاش کنید.",
                    7
                );
            } else {
                message.error(
                    "ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید — رمز عبور شما اشتباه نیست.",
                    9
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="flex justify-center items-center px-4 py-8"
            style={{
                // Not `min-h-screen`. That is 100vh, which on a phone means the screen with the
                // address bar hidden — so the gradient ran taller than the visible area and the
                // sign-in card sat slightly below the fold on first paint.
                minHeight: "var(--npe-app-h, 100dvh)",
                // Gradient follows the admin-configured brand colours instead of fixed gold/black.
                background: `linear-gradient(135deg, ${branding?.primaryColor || "#e1b137"} 0%, ${
                    branding?.secondaryColor || "#000000"
                } 100%)`,
            }}
        >
            <Card
                style={{
                    width: "100%",
                    maxWidth: "clamp(300px, 94vw, 440px)",
                    // This 24px sits on top of the card's own body padding. Doubled up on a 375px
                    // screen it was spending a quarter of the width on empty margin.
                    padding: "clamp(4px, 2vw, 24px)",
                    borderRadius: "14px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                    // Theme surface, so the card is dark in dark mode instead of a white slab.
                    backgroundColor: "var(--npe-surface)",
                }}
                bordered={false}
            >
                {/* Logo — admin-uploaded console logo wins, /logo.png is the fallback */}
                <img
                    src={branding?.consoleLogo || "/logo.png"}
                    alt={branding?.brandName || "Logo"}
                    className="mx-auto w-32 sm:w-40 mb-8 sm:mb-10"
                    style={{objectFit: "contain", maxHeight: 80}}
                />

                <Space direction="vertical" size="large" className="w-full">
                    {/* Username */}
                    <div>
                        <Text strong className="block mb-2">
                            {t("login.username") || "Username / Phone"}
                        </Text>
                        {/* A phone keyboard capitalises the first letter of a new field and runs
                            autocorrect over it. Operators sign in with an email address, so the
                            keyboard was quietly turning it into "Operator@..." and the sign-in
                            failed for a reason nothing on screen explained. `autoComplete` is set
                            to the real field name too, so a saved password can be filled in rather
                            than typed out by thumb at the start of every shift. */}
                        <Input
                            placeholder={t("login.username") || "e.g. username or phone"}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleLoginPassword();
                                }
                            }}
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            inputMode="email"
                            size="large"
                            className="rounded-md"
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <Text strong className="block mb-2">
                            {t("login.password") || "Password"}
                        </Text>
                        <Input.Password
                            placeholder={t("login.password")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleLoginPassword();
                                }
                            }}
                            autoComplete="current-password"
                            size="large"
                            iconRender={(visible) =>
                                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                            }
                            className="rounded-md"
                        />
                    </div>

                    {/* Login button */}
                    <Button
                        type="primary"
                        className="w-full text-lg py-5 rounded-md"
                        onClick={handleLoginPassword}
                        loading={loading}
                        style={{ backgroundColor: "#4b49ac", borderColor: "#4b49ac" }}
                    >
                        {t("login.login") || "Login"}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

export default Login;
